/**
 * Local record of THIS device's push registration, scoped to its owner.
 *
 * The previous shape was a bare device id under `buildlog.pushDeviceId` with no
 * owner. That made "a device id exists here" indistinguishable from "the current
 * user registered here", and the two are not the same thing: an FCM token is per
 * app install, so on a shared device the token legitimately moves between users —
 * but only when the new user asks for it. Presence-only checks turned a leftover
 * marker into consent on somebody else's behalf.
 *
 * Three distinct facts now live here:
 *  - `marker`: this user registered on this device, and the server row is theirs.
 *  - `pendingCleanup`: a server row we FAILED to delete and still owe a delete
 *    for. It is explicitly NOT consent — it grants no token sync and never
 *    reports as "registered here".
 *  - `pendingRotation`: a fresh FCM token we failed to upload. FCM signals "here is
 *    a NEW token", never "retry the last one", so a dropped token is gone for good
 *    unless it is remembered — and until it lands, delivery is aimed at a token the
 *    backend is about to retire.
 *
 * Writes NOTIFY subscribers. Two screens render "is push on here?" and localStorage
 * is not reactive, so without this a rotation failure that loses the registration
 * would leave the settings screen still saying "Enabled on this device".
 */

const STORAGE_KEY = 'buildlog.pushDevice'
/** Pre-owner-scoping key. Its value has no recorded owner, so it cannot be trusted as a marker. */
const LEGACY_DEVICE_ID_KEY = 'buildlog.pushDeviceId'
const PERSISTED_VERSION = 2

/**
 * Safety valve only. Entries are deduplicated by device id and dropped as soon as
 * a delete succeeds or comes back 403/404, so reaching this would mean many
 * distinct rows were each left un-deletable — which should not happen.
 *
 * There is deliberately NO age-based expiry. An owed delete is a security
 * obligation, and an obligation does not become safe by getting old: expiring it
 * would silently abandon a live server row that still targets a logged-out user's
 * device. Retention costs a few bytes; abandonment costs the guarantee.
 */
const MAX_PENDING_CLEANUPS = 20

export type PushDeviceMarker = {
  userId: number
  deviceId: string
}

export type PendingPushRotation = {
  userId: number
  deviceId: string
  token: string
}

export type PendingPushCleanup = {
  /** `null` means "owner unknown" — only possible for entries migrated from the legacy key. */
  userId: number | null
  deviceId: string
  recordedAt: number
}

/** Append, replacing any existing entry for the same device rather than duplicating it. */
function withCleanup(
  existing: PendingPushCleanup[],
  entry: PendingPushCleanup,
): PendingPushCleanup[] {
  return [...existing.filter(e => e.deviceId !== entry.deviceId), entry].slice(
    -MAX_PENDING_CLEANUPS,
  )
}

type PersistedShape = {
  version: number
  marker: PushDeviceMarker | null
  pendingCleanup: PendingPushCleanup[]
  pendingRotation: PendingPushRotation | null
}

const EMPTY: PersistedShape = {
  version: PERSISTED_VERSION,
  marker: null,
  pendingCleanup: [],
  pendingRotation: null,
}

/** Notified on every write, so "is push on here?" can stop being a stale read. */
const subscribers = new Set<() => void>()

export function subscribePushDeviceMarker(listener: () => void): () => void {
  subscribers.add(listener)
  return () => subscribers.delete(listener)
}

function read(): PersistedShape {
  let state: PersistedShape = EMPTY
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedShape>
      if (parsed.version === PERSISTED_VERSION) {
        state = {
          version: PERSISTED_VERSION,
          marker: parsed.marker ?? null,
          pendingCleanup: Array.isArray(parsed.pendingCleanup) ? parsed.pendingCleanup : [],
          pendingRotation: parsed.pendingRotation ?? null,
        }
      }
    }
  } catch {
    state = EMPTY
  }
  return migrateLegacy(state)
}

/**
 * A legacy id records no owner, so it must NOT become a marker — assuming the
 * current user owns it is exactly the bug this module exists to fix. It becomes
 * an owner-unknown cleanup instead: we still owe the server a delete, and the
 * attempt is harmless (the owner-only delete voter answers 403 for anyone else).
 */
function migrateLegacy(state: PersistedShape): PersistedShape {
  let legacyId: string | null = null
  try {
    legacyId = localStorage.getItem(LEGACY_DEVICE_ID_KEY)
    if (legacyId !== null) localStorage.removeItem(LEGACY_DEVICE_ID_KEY)
  } catch {
    return state
  }
  if (legacyId === null) return state

  const next: PersistedShape = {
    ...state,
    pendingCleanup: withCleanup(state.pendingCleanup, {
      userId: null,
      deviceId: legacyId,
      recordedAt: Date.now(),
    }),
  }
  write(next)
  return next
}

function write(state: PersistedShape): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } finally {
    // Notify even when persistence failed: the in-memory answer callers get from
    // `read()` is unchanged either way, and a listener that never fires is how a
    // screen ends up asserting something no longer true.
    subscribers.forEach(listener => listener())
  }
}

// localStorage unavailable means there is NO fallback: the registration simply is not
// recorded, so this device will not report as registered and Disable/logout find
// nothing to delete. `isPushStorageAvailable` lets callers tell that apart from
// "genuinely not registered" instead of reporting a false success.

/** The current owner-scoped marker, or `null` when this device has no registration. */
export function getPushDeviceMarker(): PushDeviceMarker | null {
  return read().marker
}

/**
 * Whether push is registered on this device FOR THIS USER. A marker belonging to
 * somebody else answers `false` — it is their registration, not this user's.
 */
export function isPushRegisteredForUser(userId: number | null): boolean {
  if (userId === null) return false
  const { marker } = read()
  return marker !== null && marker.userId === userId
}

export function setPushDeviceMarker(userId: number, deviceId: string): void {
  write({ ...read(), marker: { userId, deviceId } })
}

export function clearPushDeviceMarker(): void {
  write({ ...read(), marker: null })
}

/**
 * Give up the marker but remember the delete we still owe.
 *
 * Used when a logout cannot reach the server: the row must stop counting as this
 * device's registration (the next person to sign in must not inherit it) while
 * the obligation to remove it survives.
 */
export function demoteMarkerToPendingCleanup(): void {
  const state = read()
  if (state.marker === null) return
  const entry: PendingPushCleanup = {
    userId: state.marker.userId,
    deviceId: state.marker.deviceId,
    recordedAt: Date.now(),
  }
  write({ ...state, marker: null, pendingCleanup: withCleanup(state.pendingCleanup, entry) })
}

/**
 * Cleanups this user is able to action. Only the owner may delete a device
 * (PushDeviceVoter), so entries for other users stay untouched until that user
 * returns; owner-unknown entries are offered to whoever is present, since a 403
 * costs nothing and a match actually removes the row.
 */
export function getActionablePushCleanups(userId: number | null): PendingPushCleanup[] {
  if (userId === null) return []
  return read().pendingCleanup.filter(entry => entry.userId === userId || entry.userId === null)
}

export function removePushCleanup(deviceId: string): void {
  const state = read()
  write({
    ...state,
    pendingCleanup: state.pendingCleanup.filter(entry => entry.deviceId !== deviceId),
  })
}

/**
 * Whether the marker can actually be persisted. Callers that must not claim
 * success on a no-op (Disable, logout teardown) need to distinguish "nothing to
 * remove" from "cannot tell what to remove".
 */
export function isPushStorageAvailable(): boolean {
  try {
    const probe = `${STORAGE_KEY}.probe`
    localStorage.setItem(probe, '1')
    localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

/**
 * The token we failed to upload, for THIS user and THIS device row. Anything else is
 * stale — a different row or a different account — and must not be replayed.
 */
export function getPendingPushRotation(): PendingPushRotation | null {
  return read().pendingRotation
}

export function setPendingPushRotation(userId: number, deviceId: string, token: string): void {
  write({ ...read(), pendingRotation: { userId, deviceId, token } })
}

/**
 * Discharge the remembered token.
 *
 * `token` narrows it to the record the caller actually handled, so an older upload
 * finishing cannot discard a NEWER token remembered meanwhile — a rotated token has
 * no second chance of its own, because FCM only signals genuinely new ones.
 *
 * MEASURED: mutating this guard away kills no test, and that is honest rather than a
 * coverage gap. Every rotation now shares one serialization barrier
 * (`enqueueRotation`), so only one upload is ever in flight and nothing outside it
 * writes a pending record — which makes the racing state unreachable. The guard is
 * kept as the condition that stays correct if an unbarriered writer is ever added,
 * exactly like the doubled ownership checks in registerPush.ts. Do not read its
 * surviving mutation as "untested"; read it as "currently unreachable".
 */
export function clearPendingPushRotation(token?: string): void {
  const state = read()
  if (state.pendingRotation === null) return
  if (token !== undefined && state.pendingRotation.token !== token) return
  write({ ...state, pendingRotation: null })
}

/** Test seam. */
export function resetPushDeviceMarkerForTests(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(LEGACY_DEVICE_ID_KEY)
  } catch {
    // ignore
  }
}
